import { useCallback, useEffect, useState } from "react";

import clsx from "clsx";
import { formatDuration, intervalToDuration } from "date-fns";
import moment from "moment";

type Props = {
  dataProcessedAt: string;
  nextProcessedAt: string;
  timezone: string;
  className?: string;
  refreshTimeMs: number;
};

export const ProcessingStatus = ({
  className,
  dataProcessedAt,
  nextProcessedAt,
  timezone,
  refreshTimeMs,
}: Props) => {
  const [dataProcessedTimeStamp, setDataProcessedTimeStamp] = useState("");
  const [nextDataProcessingTimeStamp, setNextDataProcessingTimeStamp] =
    useState("");

  const createDataProcessedTimeStamp = useCallback(() => {
    const time = moment(dataProcessedAt).tz(timezone);
    return time.isValid() ? fromThenToNow(time.toDate()) : "Unknown";
  }, [dataProcessedAt, timezone]);

  const createNextDataProcessingTimeStamp = useCallback(() => {
    if (!nextProcessedAt) {
      return "Unknown";
    }
    const time = moment(nextProcessedAt).tz(timezone);

    // if the time is less than now
    if (time.toDate().getTime() - new Date().getTime() < 0) {
      return "a few mins";
    }

    const humanReadable = time.isValid()
      ? `${fromThenToNow(time.toDate()).replace("ago", "")}`
      : "Unknown";

    return humanReadable.includes("Now") ? "Now" : humanReadable;
  }, [nextProcessedAt, timezone]);

  useEffect(() => {
    setDataProcessedTimeStamp(createDataProcessedTimeStamp());
    setNextDataProcessingTimeStamp(createNextDataProcessingTimeStamp());

    const interval = setInterval(() => {
      setDataProcessedTimeStamp(createDataProcessedTimeStamp());
      setNextDataProcessingTimeStamp(createNextDataProcessingTimeStamp());
    }, refreshTimeMs);

    return () => clearInterval(interval);
  }, [
    createDataProcessedTimeStamp,
    createNextDataProcessingTimeStamp,
    refreshTimeMs,
  ]);

  return (
    <div className={clsx(className)}>
      <div className="text-v2-primary font-tt-commons-pro text-lg font-semibold">
        Updated at {moment(dataProcessedAt).format("MMM D, h:mm A z")}
      </div>
      <div className="text-v2-secondary font-tt-commons-pro text-xs font-normal leading-4">
        Next attribution update {nextDataProcessingTimeStamp}
      </div>
    </div>
  );
};

function fromThenToNow(from: Date) {
  const duration = intervalToDuration({
    start: from,
    end: new Date(),
  });

  const formattedDuration = formatDuration(duration, {
    format: ["days", "hours", "minutes"],
    delimiter: " and ",
  })
    .replace("hour", "hr")
    .replace("minute", "min");

  if (!formattedDuration) {
    return "Now";
  }

  return formattedDuration;
}
